import type { MentorProfileDraft } from "@/lib/api/mentor-profile";

export type SectionSummary = {
  text: string;
  checked?: boolean;
  empty?: boolean;
};

type ProfileSectionDef = {
  id:
    | "profile_photo"
    | "display_name"
    | "languages"
    | "bio"
    | "spotify"
    | "audio"
    | "vibe"
    | "interests";
  reviewSection:
    | "Profile photo"
    | "Display name"
    | "Languages"
    | "Bio"
    | "Spotify"
    | "Audio intro"
    | "My vibe"
    | "Interests";
  label: string;
  hint: string;
  summary: (draft: MentorProfileDraft) => SectionSummary;
  value: (draft: MentorProfileDraft) => unknown;
};

export const PROFILE_SECTIONS: readonly ProfileSectionDef[] = [
  {
    id: "profile_photo",
    reviewSection: "Profile photo",
    label: "Profile photo",
    hint: "The cut-out photo at the top of the profile.",
    summary: (d) =>
      d.match_card_photo_url || d.profile_picture_url
        ? { text: "Photo added", checked: true }
        : { text: "No photo yet", empty: true },
    value: (d) => ({
      profile_picture_url: d.profile_picture_url,
      match_card_photo_url: d.match_card_photo_url,
    }),
  },
  {
    id: "display_name",
    reviewSection: "Display name",
    label: "Display name",
    hint: "The name teens see at the top of the profile.",
    summary: (d) => {
      const value = d.display_name?.trim();
      return value ? { text: value } : { text: "-", empty: true };
    },
    value: (d) => d.display_name,
  },
  {
    id: "languages",
    reviewSection: "Languages",
    label: "Languages",
    hint: "Shown as quick-scan chips on the profile.",
    summary: (d) =>
      d.languages?.length
        ? { text: d.languages.join(", ") }
        : { text: "-", empty: true },
    value: (d) => d.languages,
  },
  {
    id: "bio",
    reviewSection: "Bio",
    label: "Bio",
    hint: "The short intro under the name.",
    summary: (d) => {
      const value = d.bio?.trim();
      return value ? { text: value } : { text: "-", empty: true };
    },
    value: (d) => d.bio,
  },
  {
    id: "spotify",
    reviewSection: "Spotify",
    label: "On repeat (Spotify)",
    hint: "The “On repeat” song embed.",
    summary: (d) =>
      d.favorite_music?.trim()
        ? { text: d.favorite_music }
        : { text: "-", empty: true },
    value: (d) => ({
      favorite_music: d.favorite_music,
      spotify_meta: d.spotify_meta,
    }),
  },
  {
    id: "audio",
    reviewSection: "Audio intro",
    label: "Audio intro",
    hint: "The audio intro users can play.",
    summary: (d) =>
      d.profile_audio_url
        ? { text: "Audio added", checked: true }
        : { text: "No audio yet", empty: true },
    value: (d) => d.profile_audio_url,
  },
  {
    id: "vibe",
    reviewSection: "My vibe",
    label: "My vibe",
    hint: "The vibe board — a few mood photos.",
    summary: (d) => {
      const count = d.vibe_images?.filter((i) => i.imageUrl).length ?? 0;
      return count > 0
        ? { text: `${count} photos` }
        : { text: "No photos added", empty: true };
    },
    value: (d) => d.vibe_images,
  },
  {
    id: "interests",
    reviewSection: "Interests",
    label: "Interests",
    hint: "The interests deck + the photo behind each one.",
    summary: (d) =>
      d.interests?.length
        ? { text: d.interests.join(", ") }
        : { text: "-", empty: true },
    value: (d) => ({
      interests: d.interests,
      interest_images: d.interest_images,
    }),
  },
] as const;

export type WizardBuildStep = (typeof PROFILE_SECTIONS)[number]["id"];
export type ReviewSection = (typeof PROFILE_SECTIONS)[number]["reviewSection"];

export const WIZARD_BUILD_STEPS: readonly WizardBuildStep[] =
  PROFILE_SECTIONS.map((section) => section.id);

export const REVIEW_SECTIONS: readonly ReviewSection[] = PROFILE_SECTIONS.map(
  (section) => section.reviewSection,
);

export const SECTION_BY_ID: Record<WizardBuildStep, ProfileSectionDef> =
  Object.fromEntries(
    PROFILE_SECTIONS.map((section) => [section.id, section]),
  ) as Record<WizardBuildStep, ProfileSectionDef>;

export const SECTION_BY_REVIEW: Record<ReviewSection, ProfileSectionDef> =
  Object.fromEntries(
    PROFILE_SECTIONS.map((section) => [section.reviewSection, section]),
  ) as Record<ReviewSection, ProfileSectionDef>;

export const SECTION_TO_BUILD_STEP: Record<ReviewSection, WizardBuildStep> =
  Object.fromEntries(
    PROFILE_SECTIONS.map((section) => [section.reviewSection, section.id]),
  ) as Record<ReviewSection, WizardBuildStep>;

export function isReviewSection(value: string): value is ReviewSection {
  return value in SECTION_BY_REVIEW;
}

export function sectionValue(
  draft: MentorProfileDraft,
  section: ReviewSection,
): unknown {
  return SECTION_BY_REVIEW[section].value(draft);
}

export function sectionSummary(
  draft: MentorProfileDraft,
  section: ReviewSection,
) {
  return SECTION_BY_REVIEW[section].summary(draft);
}
